import { TFunction } from "i18next";
import { ISubscriptionPaymentFailure } from "src/Utils/Hooks/ManageLicenses/useGetLicensesInfo";
import { formatDateString } from "src/Utils/DateFormatting/DateFormattter";

/**
 * Wording for the payment-failure bar, per the three states in the design.
 *
 * Which state applies is decided by the licensing service, not here. `isApproachingCancellation`
 * flips when the final reminder email is sent, so the banner starts naming the cancellation date at
 * the same moment the customer receives that date by email - and `isCancelled` wins over it,
 * because "will be cancelled on <date>" is wrong once it already has been.
 *
 * A pure function of the DTO, kept out of the component so the three states can be tested without
 * rendering the whole licences page. These strings are the only thing that tells a customer their
 * subscription is ending, so picking the wrong one is worth catching in a test.
 *
 * @param failure The clinic's payment-failure state, as reported by the licensing service.
 * @param t Translator from the caller's i18n context.
 * @returns The message to show in the bar.
 */
export const paymentFailureMessage = (failure: ISubscriptionPaymentFailure, t: TFunction): string => {
    if (failure.isCancelled) {
        return t('AuraAppSubscriptionHasBeenCancelled');
    }

    if (failure.isApproachingCancellation) {
        return t('AuraAppAccessWillBeCancelledOn', { cancellationDate: formatDateString(failure.cancellationDate) });
    }

    return t('AuraAppPaymentCouldNotBeProcessed');
};
